import { Breadcrumb, Layout, Spin, Table, Tag, Button, Input, Select, Row, Col, Card, message, Space, DatePicker, Form } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { Link } from 'react-router-dom';
import { ClearOutlined, EyeOutlined, FilterOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { APPBACKGROUND_COLOR } from '../appsettings';

const { Option } = Select;
const { RangePicker } = DatePicker;

const initialFilters = {
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: '',
};

const Invoices = () => {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    /** Fetch invoices list */
    const fetchInvoices = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                ...filters,
            });

            const response = await axiosClient.get(`/invoice/get_all_invoices.php?${params.toString()}`);
            const result = response.data;

            if (result.status === 'success') {
                setData(result.data);
                setPagination({
                    current: result.page,
                    pageSize: result.pageSize,
                    total: result.total,
                });
            } else {
                message.error(result.message || 'Failed to fetch invoices.');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            message.error(error.response?.data?.message || 'Network error.');
        } finally {
            setLoading(false);
        }
    };

    /** Load data on mount */
    useEffect(() => {
        fetchInvoices(1, 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Table pagination handlers */
    const handleTableChange = (newPagination) => {
        fetchInvoices(newPagination.current, newPagination.pageSize);
    };

    /** Clear filter */
    const clearFilters = () => {
        form.resetFields();
        setFilters(initialFilters);
        setPagination({ current: 1, pageSize: 10, total: 0 });
        fetchInvoices(1, 10);
    };

    /** Handle date range change */
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                dateFrom: dates[0].format('YYYY-MM-DD'),
                dateTo: dates[1].format('YYYY-MM-DD')
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
        }
    };

    /** Get status tag color */
    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'green';
            case 'Pending': return 'orange';
            case 'Cancelled': return 'red';
            default: return 'default';
        }
    };

    /** Table Columns */
    const columns = [
        { 
            title: 'Ref #', 
            dataIndex: 'inv_ref', 
            width: 100,
            render: (ref) => <strong>{ref}</strong>
        },
        { 
            title: 'Member', 
            dataIndex: 'member_name',
            render: (name, record) => (
                <div>
                    <div>{name}</div>
                    <small style={{ color: '#888' }}>{record.member_code}</small>
                </div>
            )
        },
        { 
            title: 'Items', 
            dataIndex: 'total_items_count',
            width: 70,
            align: 'center'
        },
        { 
            title: 'Total', 
            dataIndex: 'invoice_total',
            render: (total, record) => `${record.invoice_currency} ${Number(total).toLocaleString()}`,
            align: 'right'
        },
        { 
            title: 'Paid', 
            dataIndex: 'total_paid_amount',
            render: (paid, record) => `${record.invoice_currency} ${Number(paid).toLocaleString()}`,
            align: 'right'
        },
        { 
            title: 'Balance', 
            dataIndex: 'balance',
            render: (balance, record) => (
                <span style={{ color: balance > 0 ? '#f5222d' : '#52c41a' }}>
                    {record.invoice_currency} {Number(balance).toLocaleString()}
                </span>
            ),
            align: 'right'
        },
        {
            title: 'Status',
            dataIndex: 'inv_status',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'process_dt',
            width: 150,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A',
        },
        {
            title: 'Action',
            width: 80,
            render: (_, record) => (
                <Space>
                    <Link to={`/invoices/view/${record.inv_id}`}>
                        <Button icon={<EyeOutlined />} type="primary" size="small" />
                    </Link>
                </Space>
            )
        }
    ];

    return (
        <Spin spinning={loading} tip="Loading Invoices...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />

                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item><Link to="/invoices">Invoices</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>All Invoices</Breadcrumb.Item>
                    </Breadcrumb>

                    {/* FILTER CARD */}
                    <Card
                        style={{ marginBottom: "15px" }}
                        title="Filter"
                        extra={
                            <Row style={{ float: "right" }} gutter={[10, 10]} align="middle">
                                <Col>
                                    <Link to="/invoices/create">
                                        <Button icon={<PlusOutlined />} type="primary">Create Invoice</Button>
                                    </Link>
                                </Col>
                            </Row>
                        }
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={initialFilters}
                            onValuesChange={(changed, allValues) => {
                                if (!changed.dateRange) {
                                    setFilters(prev => ({ ...prev, ...allValues }));
                                }
                            }}
                            onFinish={() => fetchInvoices(1, pagination.pageSize)}
                        >
                            <Row gutter={[10, 10]} align="middle">

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="search" label="Search">
                                        <Input
                                            placeholder="Search by Ref or Member"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="status" label="Status">
                                        <Select>
                                            <Option value="all">All Status</Option>
                                            <Option value="Pending">Pending</Option>
                                            <Option value="Completed">Completed</Option>
                                            <Option value="Cancelled">Cancelled</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="dateRange" label="Date Range">
                                        <RangePicker 
                                            style={{ width: '100%' }}
                                            onChange={handleDateRangeChange}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button type="primary" block icon={<FilterOutlined />} htmlType="submit">
                                        Filter
                                    </Button>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button block icon={<ClearOutlined />} onClick={clearFilters}>
                                        Clear
                                    </Button>
                                </Col>

                            </Row>
                        </Form>
                    </Card>

                    {/* TABLE */}
                    <Card title="Invoices">
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey="inv_id"
                            pagination={pagination}
                            onChange={handleTableChange}
                            bordered
                            size="small"
                            scroll={{ x: 1000 }}
                        />
                    </Card>
                </Content>

                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default Invoices;
